import { formatDate } from '@angular/common'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Classification, Feature, RoutingService } from '@spartacus/core'
import { ngxCsv } from 'ngx-csv'
import { BehaviorSubject, Observable, of, Subject } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { ProductUpdate } from '../features/myAccount/product-updates/product-updates.model'
import { BaseCountry } from '../models/enum/country.enum'
import { DeliveryLeadTimeMapping, ICustomProduct } from '../models/interface/product.interface'
import { AppConfigurationService } from './app-configuration.service'
import { SharedService } from './shared/shared.service'

@Injectable()
export class ProductDetailsService {
  public maxQuantity = 9999
  public redirectToReviews: Subject<void> = new Subject<void>()

  public productDetailSource = new BehaviorSubject<ICustomProduct>({})
  private productDetail$ = this.productDetailSource.asObservable()

  constructor(
    private appConfigurationService: AppConfigurationService,
    private http: HttpClient,
    public sharedService: SharedService,
    public routingService: RoutingService
  ) {}

  public getProductDetail$(): Observable<ICustomProduct> {
    return this.productDetail$
  }

  public getProductDetail(): Observable<any> {
    return this.routingService.getRouterState().pipe(
      map((state) => state.state.params?.productCode),
      switchMap((productCode: string) => {
        const baseUrl = this.appConfigurationService.getBaseSiteUrl()
        const url =
          baseUrl +
          `/products/${productCode}?fields=DEFAULT,classifications,code,name,summary,price(formattedValue,DEFAULT),images(galleryIndex,FULL),averageRating,stock(DEFAULT),description,availableForPickup,url,numberOfReviews,manufacturer,categories(FULL),priceRange,multidimensional,configuratorType,configurable,tags,videoURL,productSize,uomMappings,unit,certificationLogos,environmentalInfo,newProduct,changedProduct,replacingPartNo,dangerousGoods,dangerousGoodsRegCode,dangerousGoodsUNPSN,dangerousGoodsPackingGrp,dangerousGoodsClass,dangerousGoodsShipping,hazchemCode,hazchemTemp,dgPackageDescription,dgPackageWeight,sdsIssueDate,sdsExpiryDate,hazardous,rawMaterial,rawMaterialCountry,unspscCode,waist,shelfLife,recyclable,wetnessIndicator,benefits,marketingText,brand,exWorkDays,minPurchaseApply,preDeliveryCheck,deliveryLeadTimeMappings,gstApplicable,sustainabilityLabels,packagingMaterial,discontinuedProduct,replacementProduct`
        return this.http.get<any>(url).pipe(
          tap((product) => this.productDetailSource.next(product)),
          catchError(() => of({}))
        )
      })
    )
  }

  public getProductDetailByProductCode(productCode: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url =
      baseUrl +
      `/products/${productCode}?fields=DEFAULT,classifications,code,name,summary,price(formattedValue,DEFAULT),images(galleryIndex,FULL),averageRating,stock(DEFAULT),description,availableForPickup,url,numberOfReviews,manufacturer,categories(FULL),priceRange,multidimensional,configuratorType,configurable,tags,videoURL,productSize,uomMappings,unit,certificationLogos,environmentalInfo,newProduct,changedProduct,replacingPartNo,dangerousGoods,dangerousGoodsRegCode,dangerousGoodsUNPSN,dangerousGoodsPackingGrp,dangerousGoodsClass,dangerousGoodsShipping,hazchemCode,hazchemTemp,dgPackageDescription,dgPackageWeight,sdsIssueDate,sdsExpiryDate,hazardous,rawMaterial,rawMaterialCountry,unspscCode,waist,shelfLife,recyclable,wetnessIndicator,benefits,marketingText,brand,exWorkDays,minPurchaseApply,preDeliveryCheck,deliveryLeadTimeMappings,gstApplicable,sustainabilityLabels,packagingMaterial,discontinuedProduct,replacementProduct,discountinuedProductRegion`

    return this.http.get<any>(url).pipe(
      catchError((error) => {
        throw error
      })
    )
  }

  public getProduct(code: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = baseUrl + `/products/${code}`
    return this.http.get<any>(url).pipe(
      map((res) => !!res),
      catchError(() => of(false))
    )
  }

  /**
   * Given a product code, the replacement product information is returned
   * @param productCode current product code
   * @returns replacement product information
   */
  public getReplacementProduct(productCode: string): Observable<ICustomProduct> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url =
      baseUrl + `/products/${productCode}?fields=discontinuedProduct,replacementProduct,discountinuedProductRegion`

    return this.http.get<ICustomProduct>(url).pipe(
      catchError((error) => {
        throw error
      })
    )
  }

  public getProductUpdates(country: string): Observable<ProductUpdate[]> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = baseUrl + '/products/updates'
    const params = new HttpParams().set('country', country.toLowerCase())

    return this.http.get<{ results: ProductUpdate[] }>(url, { params }).pipe(
      map((product) => product.results),
      catchError((error) => {
        throw error
      })
    )
  }

  public downloadNewLineForm(product: ICustomProduct): void {
    let pallet = 'aupallet'
    if (this.sharedService.baseSiteId === BaseCountry.NZ) {
      pallet = 'nzpallet'
    }

    const data = [
      {
        newProduct: product.newProduct,
        changedProduct: product.changedProduct === 'No' ? 'No' : `Yes | ${product.replacingPartNo}`,
        productCode: product?.code ? product.code : '',
        productName: product?.name ? product.name : '',
        productDescription: product?.summary ? product.summary : '',
        brand: product?.brand ? product.brand : '',
        colour: product?.colour ? product.colour : '',
        packagingMaterial: product?.unit?.name ? product.unit.name : '',
        innerUnitOfMeasure: product?.packagingMaterial ? product.packagingMaterial : '',
        innerUOMQuantity: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerinneruomquantity'
        ),
        innerBarCode: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerunitean'
        ),
        innerLength: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerunitlength'
        ),
        innerWidth: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerunitwidth'
        ),
        innerHeight: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerunitheight'
        ),
        innerCube: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerunitvolume'
        ),
        innerWeight: this.getFeatureByCode(
          product?.classifications,
          'consumerUnits',
          'asaleocareClassification/1.0/consumerUnits.consumerunitgrossweight'
        ),
        cartonBarCode: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportunitean'
        ),
        uomQuantity: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportuomquantity'
        ),
        cartonLength: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportunitlength'
        ),
        cartonWidth: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportunitwidth'
        ),
        cartonHeight: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportunitheight'
        ),
        cartonCubic: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportunitvolume'
        ),
        cartonWeight: this.getFeatureByCode(
          product?.classifications,
          'transportUnits',
          'asaleocareClassification/1.0/transportUnits.transportunitgrossweight'
        ),
        uomPerPallet: this.getFeatureByCode(
          product?.classifications,
          pallet,
          `asaleocareClassification/1.0/${pallet}.${pallet}nooflayers`
        ),
        uomPerLayer: this.getFeatureByCode(
          product?.classifications,
          pallet,
          `asaleocareClassification/1.0/${pallet}.${pallet}uomperlayer`
        ),
        palletLength: this.getFeatureByCode(
          product?.classifications,
          pallet,
          `asaleocareClassification/1.0/${pallet}.${pallet}unitlength`
        ),
        palletWidth: this.getFeatureByCode(
          product?.classifications,
          pallet,
          `asaleocareClassification/1.0/${pallet}.${pallet}unitwidth`
        ),
        palletHeight: this.getFeatureByCode(
          product?.classifications,
          pallet,
          `asaleocareClassification/1.0/${pallet}.${pallet}unitheight`
        ),
        palletWeight: this.getFeatureByCode(
          product?.classifications,
          pallet,
          `asaleocareClassification/1.0/${pallet}.${pallet}unitgrossweight`
        ),
        countryOfOrigin: product?.countryOfOrigin ? product.countryOfOrigin : '',
        gstApplicable: product?.gstApplicable ? product.gstApplicable : '',
        exWorkDays: product?.exWorkDays ? product?.exWorkDays : '',
        deliveryTimeMelbourne: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Melbourne'),
        deliveryTimeSydney: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Sydney'),
        deliveryTimeBrisbane: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Brisbane'),
        deliveryTimeMackay: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Msckay'),
        deliveryTimeCairns: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Cairns'),
        deliveryTimeTownsville: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Townsvile'),
        deliveryTimeAdelaide: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Adelaide'),
        deliveryTimeDarwin: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Darwin'),
        deliveryTimePerth: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Perth'),
        deliveryTimeHobart: this.getDeliveryLeadTimeByName(product?.deliveryLeadTimeMappings, 'Hobart'),
        minPurchaseApply: product?.minPurchaseApply ? product.minPurchaseApply : '',
        preDeliveryCheck: product?.preDeliveryCheck ? product.preDeliveryCheck : '',
        minOrderQuantity: product?.minOrderQuantity ? product.minOrderQuantity : '',
        dangerousGoods: product?.dangerousGoods ? product.dangerousGoods : '',
        hazardous: product?.hazardous ? product.hazardous : '',
        dangerousGoodsRegCode: product?.dangerousGoodsRegCode ? product.dangerousGoodsRegCode : '',
        dangerousGoodsUNPSN: product?.dangerousGoodsUNPSN ? product.dangerousGoodsUNPSN : '',
        dangerousGoodsPackingGrp: product?.dangerousGoodsPackingGrp ? product.dangerousGoodsPackingGrp : '',
        dangerousGoodsClass: product?.dangerousGoodsClass ? product.dangerousGoodsClass : '',
        hazchemCode: product?.hazchemCode ? product.hazchemCode : '',
        dangerousGoodsShipping: product?.dangerousGoodsShipping ? product.dangerousGoodsShipping : '',
        hazchemTemp: product?.hazchemTemp ? product.hazchemTemp : '',
        dgPackageDescription: product?.dgPackageDescription ? product.dgPackageDescription : '',
        dgPackageWeight: product?.dgPackageWeight ? product.dgPackageWeight : '',
        dgPackageWeightUnit: product?.dgPackageWeightUnit ? product.dgPackageWeightUnit : '',
        sdsIssueDate: product?.sdsIssueDate ? product.sdsIssueDate : '',
        sdsExpiryDate: product?.sdsExpiryDate ? product.sdsExpiryDate : '',
        sustainabilityLabels: product?.sustainabilityLabels ? product.sustainabilityLabels : '',
        rawMaterial: product?.rawMaterial ? product.rawMaterial : '',
        rawMaterialCountry: product?.rawMaterialCountry ? product.rawMaterialCountry : '',
        unspscCode: product?.unspscCode ? product.unspscCode : '',
        waist: product?.waist ? product.waist : '',
        absorbencyCapacity: product?.absorbencyCapacity ? product.absorbencyCapacity : '',
        wetnessIndicator: product?.wetnessIndicator ? product.wetnessIndicator : '',
        shelfLife: product?.shelfLife ? product.shelfLife : '',
        recyclable: product?.recyclable ? product.recyclable : '',
        benefits: product?.benefits ? product.benefits : '',
        marketingText: product?.marketingText ? product.marketingText : ''

        // baseUnitofMeasure: (product?.unit?.name)? product.unit.name : '',
        // uomMappings:this.getProductUomMappings(product?.uomMappings),
        // youtube_Video:(product?.videoURL)? product.videoURL : '',
        // manufacturer:(product?.manufacturer)? product.manufacturer : '',
        // category_Code:this.getProductCategoryCode(product?.categories),
        // category_Name:this.getProductCategoryName(product?.categories),
        // price:(product?.price?.formattedValue)? product?.price?.formattedValue : '',
        // stockStatus:(product?.stock?.stockLevelStatus)? product?.stock?.stockLevelStatus : '',
        // productDesignLine: (product?.productDesignLine)? product.productDesignLine : '',
        // model: (product?.model)? product.model : '',
        // length: (product?.length)? product.length : '',
        // productSize: (product?.productSize)? product.productSize : '',
        // deliveryTime: (product?.deliveryTime)? product.deliveryTime : '',
        // similarProducts: this.getProductReferenceData(product?.code,'SIMILAR'),
        // compatibleProducts: this.getProductReferenceData(product?.code,'COMPATIBLE'),
        // descriptiveColour: (product?.colour)? product?.colour : '',
      }
    ]
    const todayDate = formatDate(new Date(), 'yyyy/MM/dd', 'en')
    const fileName = 'New line form_' + todayDate
    const options = {
      title: 'Asaleo  Personal Care Pty Ltd\nAsaleo Care is HACCP accredited',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: true,
      useBom: true,
      headers: [
        'New Product',
        'Is this product replacing an existing product?',
        'Manufacturer Product Code',
        'Product Name',
        'Product Description - (Long Description)',
        'Brand',
        'Descriptive Colour',
        'Packaging Material',
        'Inner Unit of Measure',
        'Inner UOM quantity',
        'Inner Bar code',
        'Inner Length (m)',
        'Inner Width (m)',
        'Inner Height (m)',
        'Inner Cubic',
        'Inner Weight',
        'Carton Barcode',
        'UOM Quantity | Inner per Carton',
        'Carton (Shipper) Length (m)',
        'Carton (Shipper) Width (m)',
        'Carton (Shipper) Height (m)',
        'Carton Cubic',
        'Carton Weight',
        'Number of UOM (cartons) per pallet',
        'UOM per Layer (pallet)',
        'Pallet Length (m)',
        'Pallet Width (m)',
        'Pallet Height (m)',
        'Pallet Weight include pallet',
        'Country of Origin',
        'GST Applicable (Yes/No)',
        'EX Works Days',
        'Delivery lead time in days to Melbourne',
        'Delivery lead time in days to Sydney',
        'Delivery lead time in days to Brisbane',
        'Delivery lead time in days to Mackay',
        'Delivery lead time in days to Cairns',
        'Delivery lead time in days to Townsville',
        'Delivery lead time in days to Adelaide',
        'Delivery lead time in days to Darwin',
        'Delivery lead time in days to Perth',
        'Delivery lead time in days to Hobart',
        'Does minimum purchase apply?',
        'Pre delivery check requirements',
        'Minimum order quantity',
        'Product Classified as Dangerous Goods Yes/No',
        'Hazardous (Y/N)',
        'Dangerous goods regulation code',
        'QA.Dangerous Goods UN Number and PSN',
        'QA.Dangerous Goods Packing Group',
        'QA.Dangerous Goods Class / Division (Main Hazard)',
        'Hazchem code',
        'Dangerous goods shipping name',
        'Haz Flash Point Celsius',
        'DG package description',
        'DG Weight',
        'DG Weight Unit Litre/Kg',
        'SDS Issue Date',
        'Expiry date of SDS',
        'Environment or Sustainable Certifications? What are they?',
        'Raw material supplied',
        'Raw material country of origin',
        'UNSPSC code',
        'Waist (cm)',
        'Absobency capacity',
        'Wetness indicator',
        'What Is The Shelf Life weeks or days?',
        '% Recyclable Content',
        'Key Selling Points/Features/Benefits',
        'Marketing Text/Product details'

        // 'Base unit of measurement',
        // 'UOM mapping',
        // 'Video link',
        // 'Manufacturer',
        // 'Category code',
        // 'Category name',
        // 'Price',
        // 'Stock status',
        // 'Product design line',
        // 'Model',
        // 'Length',
        // 'product size',
        // 'Delivery time',
        // 'Similar products',
        // 'Compatible products',
        // 'Descriptive colour',
      ]
    }
    new ngxCsv(data, fileName, options)
  }

  public getFeatureByCode(classifications: Classification[], code: string, featureCode?: string): string {
    if (classifications && classifications.length > 0) {
      const classification: Classification = classifications.find((obj) => {
        return obj.code === code
      })
      if (classification) {
        const features: Feature[] = classification.features
        if (featureCode) {
          const feature = features.find((item) => item.code === featureCode)
          if (feature) {
            return feature.featureValues[0]?.value
          }
          return ''
        }
        return features[0]?.featureValues[0]?.value
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  public getDeliveryLeadTimeByName(deliveryLeadTimeMappings: DeliveryLeadTimeMapping[], name: string): string {
    if (deliveryLeadTimeMappings && deliveryLeadTimeMappings.length > 0) {
      const deliveryLeadTimeMapping: DeliveryLeadTimeMapping = deliveryLeadTimeMappings.find((obj) => {
        return obj.name === name
      })
      if (deliveryLeadTimeMapping) {
        return deliveryLeadTimeMapping.deliveryLeadTime
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  public getUomPerPallet(product: ICustomProduct): string {
    const unit = this.getBaseSiteFromUnit()
    if (product.uomMappings?.length) {
      for (const itemUOM of product.uomMappings) {
        if (itemUOM.fromUnit === unit) {
          return itemUOM.qtyConversion.toString()
        }
      }
    }
    return ''
  }

  public getUomPerLayer(product: ICustomProduct): string {
    if (product.uomMappings?.length) {
      for (const itemUOM of product.uomMappings) {
        if (itemUOM.fromUnit === 'OA') {
          return itemUOM.qtyConversion.toString()
        }
      }
    }
    return ''
  }

  public getNumberOfLayers(product: ICustomProduct): string {
    const unit = this.getBaseSiteFromUnit()
    if (product.uomMappings?.length) {
      for (const itemUOM of product.uomMappings) {
        if (itemUOM.fromUnit === unit && itemUOM.toUnit === 'OA') {
          return itemUOM.qtyConversion.toString()
        }
      }
    }
    return ''
  }

  private getBaseSiteFromUnit(): string {
    let unit = 'PLA'
    if (this.sharedService.baseSiteId === BaseCountry.NZ) {
      unit = 'PLZ'
    }
    return unit
  }
}
